import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EXTRA_USER_GROSS_MONTH_CENTS, formatCents, type SeatOverview } from '@kontor/shared';
import { api, ApiError } from '../api';
import { useSession } from '../session';
import { SeatsBookDialog } from './SeatsBookDialog';
import './seats.css';

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const day = (iso: string) => dateFormat.format(new Date(`${iso}T12:00:00`));

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);

/**
 * Plätze und Zusatzbenutzer in „Wirkia Konto“ (Änderungswunsch vom 21.10.2026): Dort verwaltet der Mandant sein Paket und seine Zusatzbuchungen. Die Karte zeigt, wie viele Plätze belegt
 * sind, listet die gebuchten Zusatzbenutzer und kündigt sie zum Ende des bezahlten Monats. Neue Zusatzbenutzer bucht man in einem Fenster in drei Schritten (`SeatsBookDialog`), das
 * auch die Benutzerverwaltung öffnet. Buchen und Kündigen verlangt das Recht `setup.billing`.
 */
export function SeatsCard() {
  const { can } = useSession();
  const client = useQueryClient();
  const mayBook = can('setup.billing');
  const [booking, setBooking] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const seats = useQuery({ queryKey: ['seats'], queryFn: async () => (await api<{ data: SeatOverview }>('GET', '/billing/seats')).data });

  const refresh = () => {
    client.invalidateQueries({ queryKey: ['seats'] });
    client.invalidateQueries({ queryKey: ['users'] });
    client.invalidateQueries({ queryKey: ['billing'] });
  };
  const cancel = useMutation({
    mutationFn: (id: string) => api('DELETE', `/billing/seats/${id}`),
    onSuccess: () => {
      refresh();
      setMessage({ ok: true, text: 'Die Kündigung ist eingegangen. Die Plätze bleiben bis zum Ende des bezahlten Monats.' });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Die Kündigung hat nicht geklappt.') }),
  });

  const data = seats.data;
  if (!data) return null;
  const extra = data.total - data.included;

  return (
    <section className="card seats" aria-labelledby="seats-title">
      <div className="card-head">
        <h2 id="seats-title">Plätze und Zusatzbenutzer</h2>
        {mayBook && data.canBook && <button type="button" className="btn btn--primary btn--sm" onClick={() => setBooking(true)}>Benutzerlizenzen kaufen</button>}
      </div>
      <p className="muted small">
        {data.used} von {data.total} Plätzen belegt. Im Paket {data.included === 1 ? 'ist ein Benutzer' : `sind ${data.included} Benutzer`} enthalten{extra > 0 ? `, dazu ${extra} gebuchte Zusatzbenutzer` : ''}. Jeder weitere Benutzer kostet {formatCents(EXTRA_USER_GROSS_MONTH_CENTS)} brutto im Monat. Die Benutzer selbst verwalten Sie unter Einrichtung → Benutzer.
      </p>
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}

      {data.bookings.length === 0 && <p className="muted small">Sie haben keine Zusatzbenutzer gebucht.</p>}
      {data.bookings.length > 0 && (
        <ul className="seats__list">
          {data.bookings.map((b) => (
            <li key={b.id} className="seats__item">
              <div>
                <strong>{b.quantity} Zusatzbenutzer</strong> seit {day(b.startsOn)}, {formatCents(b.grossMonthCents)} brutto im Monat
                <div className="muted small">
                  {b.status === 'cancelling' && b.cancelAt ? `Gekündigt, die Plätze laufen bis ${day(b.cancelAt)}.` : b.paidUntil ? `Bezahlt bis ${day(b.paidUntil)}.` : 'Die erste Rechnung folgt.'}
                </div>
              </div>
              {mayBook && b.status === 'active' && (
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  disabled={cancel.isPending}
                  onClick={() => {
                    if (window.confirm(`${b.quantity} Zusatzbenutzer zum Ende des bezahlten Monats kündigen?

Bis ${b.paidUntil ? day(b.paidUntil) : 'dahin'} bleiben die Plätze. Danach können nicht mehr Benutzer aktiv sein, als Ihr Paket Plätze hat.`)) cancel.mutate(b.id);
                  }}
                >
                  Kündigen
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {mayBook && !data.canBook && <p className="muted small">Zusatzbenutzer lassen sich buchen, sobald Ihr Paket bestellt und bezahlt ist.</p>}
      {!mayBook && <p className="muted small">Zusatzbenutzer buchen und kündigen kann nur, wer die Abrechnung verwalten darf. Fragen Sie den Inhaber.</p>}
      {booking && <SeatsBookDialog onClose={() => setBooking(false)} />}
    </section>
  );
}
